/* ⚠️ GENERATED from app/services/messageOrigin.ts by gen-twins.mjs — edit the TypeScript, not this. */
// ════════════════════════════════════════════════════════════════════════
// services/messageOrigin.ts — which of MY devices a message was sent from,
// and whether this client should say so.
//
// "A message I sent from the app has no icon in the app, and a phone icon in
//  the web. A message I sent from the web has no icon in the web, and a web
//  icon in the app." — one rule, read by both clients (allo-web gets a
//  generated twin), so the two can never disagree about the same bubble.
//
// 🟢 HOW A MESSAGE SAYS WHERE IT CAME FROM, strongest first:
//    1. `sentFrom: 'web' | 'app'` — written by every send since this shipped.
//    2. `senderDeviceId` — a browser's device id is minted as `web_…` (allo-web
//       crypto/identity.js), so it answers for every SEALED message ever sent,
//       long before `sentFrom` existed. Anything else is a phone.
//    3. neither → unknown, and unknown draws nothing. A wrong icon is worse than
//       none.
//
// 🟢 ONLY MY OWN MESSAGES. Which device somebody ELSE used is not something
//    either client shows, and system rows (joins, calls, security cards) were not
//    typed on any device of mine at all.
// ════════════════════════════════════════════════════════════════════════

/** A browser's device id — see allo-web/src/crypto/identity.js newDeviceId. */
export const WEB_DEVICE_PREFIX = 'web_';

/** Rows that are not a person typing on a device. */
const NOT_TYPED = new Set(['system', 'security', 'call', 'call_log', 'reaction_action', 'vote_action', 'support']);

/** Where was this message sent from, as far as the message itself can tell. */
export function messageOrigin(msg) {
  if (!msg) return null;
  const s = msg.sentFrom;
  if (s === 'web' || s === 'app') return s;
  const d = typeof msg.senderDeviceId === 'string' ? msg.senderDeviceId : '';
  if (!d) return null;
  return d.startsWith(WEB_DEVICE_PREFIX) ? 'web' : 'app';
}

/**
 * The icon this client draws beside one of MY messages, or null.
 *   app client → 'web'   for what I sent from a browser
 *   web client → 'phone' for what I sent from the app
 * and nothing for what I sent from the client I am looking at.
 */
export function originBadge(msg, myUid, client) {
  if (!msg || !myUid || msg.senderId !== myUid) return null;
  if (NOT_TYPED.has(String(msg.type || ''))) return null;
  const o = messageOrigin(msg);
  if (!o || o === client) return null;
  return o === 'web' ? 'web' : 'phone';
}

/**
 * Was this message sent by ME, from a device other than this one?
 *
 * 🟥 THE QUESTION EVERY "skip my own messages" SHORTCUT FORGOT TO ASK. A message
 *    I sent from THIS phone has a plaintext copy in this phone's database, so the
 *    sealed cloud copy can be ignored. A message I sent from the browser has no
 *    copy here at all — it IS the sealed cloud copy — and skipping it is what drew
 *    "🔒" (or nothing) for everything typed on the web.
 */
export function fromMyOtherDevice(msg, myUid, myDeviceId) {
  if (!msg || !myUid || msg.senderId !== myUid) return false;
  const d = typeof msg.senderDeviceId === 'string' ? msg.senderDeviceId : '';
  if (d && myDeviceId) return d !== myDeviceId;
  /* no device id on the row (a plaintext kind, e.g. a flight card): only an
     explicit "sent from the web" marks it as not mine-from-here */
  return msg.sentFrom === 'web';
}

/* ═══════════════════════════════════════════════════════════════════════════
   🟥 THE RECIPIENT DELETED MY COPY BEFORE MY OTHER DEVICE COULD READ IT.
   Store-and-forward has the RECIPIENT sweep a delivered message out of the
   cloud. It only ever asked about the recipient's own devices — so a message I
   sent from the web was destroyed by my friend's phone within a second, and my
   own phone, switched off, never had a source for it at all.

   🟢 SO A RECIPIENT LEAVES A FRESH MESSAGE IN THE CLOUD WHILE ITS SENDER HAS
      ANOTHER DEVICE that may still need it — for a week, after which it is
      swept as before (and the mailbox copy, which is kept 30 days, covers the
      rest). Bounded, so a multi-device sender does not mean a forever cloud.
   ═══════════════════════════════════════════════════════════════════════════ */
export const SENDER_SYNC_HOLD_MS = 7 * 24 * 60 * 60 * 1000;
function tsMs(t) {
  if (!t) return 0;
  if (typeof t === 'number') return t;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  if (typeof t._seconds === 'number') return t._seconds * 1000;
  const d = new Date(t).getTime();
  return Number.isFinite(d) ? d : 0;
}

/** May the recipient NOT sweep this yet, because its sender has another device? */
export function holdForSenderDevices(msg, senderDeviceCount, now = Date.now()) {
  if (!(Number(senderDeviceCount) > 1)) return false;
  const at = tsMs(msg?.timestamp);
  if (!at) return true; // a pending server time is as fresh as it gets
  return now - at < SENDER_SYNC_HOLD_MS;
}
